import React, { useState, useCallback, useRef, useEffect } from 'react';
import Icon from '../Icon';
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { pdfjs, Document, Page } from 'react-pdf';
import { useWindowSize } from '../../hooks/useWindowSize';
import Thumbnail from './components/Thumbnail'
import { PdfLink } from "../../pages/Group/Modules/components/SelectedModule/modules/Post/styles";
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import Spinner from '../Loader';
import GoBackIconSVG from '../../assets/icons/go-back-arrow.svg';
import GoFowardIconSVG from '../../assets/icons/go-foward-arrow.svg';
import ZoomInSVG from '../../assets/icons/zoom_in.svg';
import downloadFileIcon from "../../assets/icons/download-file.svg";
import ZoomOutSVG from '../../assets/icons/zoom_out.svg';
import CollapsePdfSVG from '../../assets/icons/collapse.svg';
import SizePdfSVG from '../../assets/icons/size.svg';
import PDFLoader from './components/Loader';

import {
  MainWrapper,
  Toolbar,
  Tools,
  ThumbnailsSidebar,
  PreviewMainWrapper,
  PreviewWrapper,
  ThumbnailsWrapper,
  ThumbnailsList,
  Scroll,
  Button,
  Input,
  Pagination,
  ZoomControl,
  LoaderWrapper,
  Loader,
  LoaderText,
  LoaderTextBold,
  DownloadLink,
  PdfDownloadBlockedStyles,
} from './styles';
import slugify from 'slugify';
import { useTheme, createGlobalStyle } from 'styled-components';

const DEFAULT_ZOOM_DELTA = 0.05;
const DEFAULT_SCALE = 1;

interface PdfViewerProps {
  fileUrl: string;
  fileName: string;
  blockDownloadFiles?: boolean;
  isOwner?: boolean;
  key?: string;
}


pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;


const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, fileName, blockDownloadFiles, isOwner = false, key }) => {
  // Debug log para verificar las props recibidas
  // console.log('📄 PdfViewer - Props recibidas:', {
  //   blockDownloadFiles,
  //   isOwner,
  //   shouldShowDownload: (!blockDownloadFiles || isOwner)
  // });

  const { isMobile, isDesktop } = useWindowSize();

  // Estilos globales para el bloqueo de descargas
  const GlobalStyles = createGlobalStyle`${PdfDownloadBlockedStyles}`;
  const theme = useTheme() as { colors: { Primary: string } }
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mainWrapRef = useRef<HTMLDivElement>(null);
  const thumbnailsListRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isPdfLibrarySupported, setIsPdfLibrarySupported] = useState<boolean>(true)
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pdfLoadingPercent, setPdfLoadingPercent] = useState<number>(0);
  const [pdfError, setPdfError] = useState<string>('')
  const [pdfZoom, setPdfZoom] = useState<number>(1);
  const [pdfWidth, setPdfWidth] = useState<number>(0);
  const [pdfHeight, setPdfHeight] = useState<number>(0);
  const handle = useFullScreenHandle();

  const INITIAL_PAGES = isMobile ? 1 : 10000;
  const PADDING_PAGES = INITIAL_PAGES - 1;

  const calculateZoom = useCallback((pdfWidth: number) => {
    const containerWidth = isMobile ? 355 : 670;
    return containerWidth / pdfWidth;
  }, [isMobile]);

  const scrollToThumbnail = (pageNumber: number) => {
    if (thumbnailsListRef && thumbnailsListRef?.current) {
      const thumbnails = thumbnailsListRef?.current.querySelectorAll('.thumbnail');
      if (thumbnails.length >= pageNumber) {
        const selectedThumbnail = thumbnails[pageNumber - 1];
        selectedThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        // // Function to go to the next page

        // const goToNextPage = useCallback(async () => {
        //   if (!pdfDoc || pdfIsLoading) return;

        //   const nextPageNumber = currentPage + 1;

        //   if (nextPageNumber > totalPages) return; // Evita ir más allá de la última página

        //   // Cargar la página PADDING_PAGES adelante
        //   if (nextPageNumber <= totalPages - PADDING_PAGES) {
        //     const futurePageNumber = nextPageNumber + PADDING_PAGES;

        //     if (futurePageNumber <= totalPages) {
        //       const page = await pdfDoc.getPage(futurePageNumber);
        //       const canvasPage = await renderPdfPage(page, futurePageNumber);

        //       if (canvasPage) {
        //         setCanvasPages((oldState) => {
        //           const updatedCanvasPages = [...oldState.slice(1), canvasPage];
        //           return updatedCanvasPages;
        //         });
        //       }

      }
    }
  };

  const scrollToSelectedPage = (pageNumber) => {
    if (scrollRef && scrollRef?.current) {
      const pageElements = scrollRef.current.querySelectorAll('.pdf-page');
      if (pageElements.length > pageNumber) {
        const containerHeight = scrollRef.current.clientHeight;
        const itemHeight = scrollRef.current.scrollHeight / pageElements.length;
        const scrollTop = pageNumber * itemHeight - containerHeight + itemHeight + 45.5;
        scrollRef.current.scrollTop = scrollTop;
      }
    }
  };

  // Handlers para paginación
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // useEffect(() => {
  //   // Detección básica de soporte para PDF.js
  //   try {
  //     const isSupported = !!(window?.Uint8Array && window?.Worker && window?.Blob);
  //     setIsPdfLibrarySupported(isSupported);
  //   } catch (err) {
  //     setIsPdfLibrarySupported(false);
  //   }
  // }, []);


  const zoomIn = () => {
    setPdfZoom(prev => Number((prev + DEFAULT_ZOOM_DELTA).toFixed(2)));
  };

  const zoomOut = () => {
    setPdfZoom(prev => Number((prev - DEFAULT_ZOOM_DELTA).toFixed(2)));
  };

  const handleInputBlur = () => {
    if (inputRef?.current?.value) {
      const pageNumber = parseInt(inputRef.current.value, 10);
      goToPage(pageNumber);
      scrollToSelectedPage(pageNumber);
    }
  };

  // Renderizado de cada página del PDF
  const onLoadSuccess = async (pdf: pdfjs.PDFDocumentProxy) => {
    setTotalPages(pdf.numPages);

    // Obtener el tamaño de la primera página
    const page = await pdf.getPage(1);
    const { width, height } = page.getViewport({ scale: 1 });

    setCurrentPage(1);
    setPdfZoom((prevZoom) => (prevZoom === 1 && !isFullScreen ? calculateZoom(width) : prevZoom));
    setPdfWidth(width);
    setPdfHeight(height);
  };

  const enterFullScreen = async () => {
    try {
      await handle.enter();
      setIsFullScreen(true);
    } catch (error) {
      console.error("Error entering full-screen mode:", error);
    }
  };

  const exitFullScreen = async () => {
    try {
      await handle.exit();
      setIsFullScreen(false);
    } catch (error) {
      console.error("Error exiting full-screen mode:", error);
    }
  };

  // Efecto para desplazarse automáticamente a la miniatura seleccionada
  useEffect(() => {
    scrollToThumbnail(currentPage);
  }, [currentPage]);

  // Efecto para aplicar bloqueo de eventos cuando no se debe permitir descarga
  useEffect(() => {
    const shouldBlock = blockDownloadFiles && !isOwner;

    if (shouldBlock) {
      // Aplicar event listeners solo a las páginas del PDF y thumbnails
      const applyBlockToElement = (element: Element) => {
        const handleContextMenu = (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };

        const handleDragStart = (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };

        const handleSelectStart = (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };

        const handleCopy = (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };

        const handleCut = (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };

        // Aplicar event listeners al elemento
        element.addEventListener('contextmenu', handleContextMenu);
        element.addEventListener('dragstart', handleDragStart);
        element.addEventListener('selectstart', handleSelectStart);
        element.addEventListener('copy', handleCopy);
        element.addEventListener('cut', handleCut);

        // Retornar función de cleanup
        return () => {
          element.removeEventListener('contextmenu', handleContextMenu);
          element.removeEventListener('dragstart', handleDragStart);
          element.removeEventListener('selectstart', handleSelectStart);
          element.removeEventListener('copy', handleCopy);
          element.removeEventListener('cut', handleCut);
        };
      };

      // Aplicar bloqueo a las páginas del PDF
      const pdfPages = document.querySelectorAll('.react-pdf__Page');
      const cleanupFunctions: (() => void)[] = [];

      console.log('📄 PdfViewer - Aplicando bloqueo a páginas:', {
        totalPages: pdfPages.length,
        shouldBlock
      });

      pdfPages.forEach((page, index) => {
        const cleanup = applyBlockToElement(page);
        if (cleanup) cleanupFunctions.push(cleanup);
        console.log(`📄 PdfViewer - Página ${index + 1} bloqueada`);
      });

      // Aplicar bloqueo a los thumbnails
      const thumbnails = document.querySelectorAll('.thumbnail .react-pdf__Page');

      console.log('📄 PdfViewer - Aplicando bloqueo a thumbnails:', {
        totalThumbnails: thumbnails.length,
        shouldBlock
      });

      thumbnails.forEach((thumbnail, index) => {
        const cleanup = applyBlockToElement(thumbnail);
        if (cleanup) cleanupFunctions.push(cleanup);
        console.log(`📄 PdfViewer - Thumbnail ${index + 1} bloqueado`);
      });

      // Cleanup
      return () => {
        cleanupFunctions.forEach(cleanup => cleanup());
      };
    }
  }, [blockDownloadFiles, isOwner]);

  // Efecto para aplicar clases CSS de bloqueo
  useEffect(() => {
    const shouldBlock = blockDownloadFiles && !isOwner;

    if (shouldBlock) {
      // Agregar clases de bloqueo al body para aplicar estilos globales
      document.body.classList.add('pdf-download-blocked');
    } else {
      // Remover clases de bloqueo
      document.body.classList.remove('pdf-download-blocked');
    }

    // Cleanup
    return () => {
      document.body.classList.remove('pdf-download-blocked');
    };
  }, [blockDownloadFiles, isOwner]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.value = String(currentPage);
    }
  }, [inputRef, currentPage, totalPages]);

  return (
    <>
      <GlobalStyles />
      {isPdfLibrarySupported ? (
        <Document
          file={fileUrl}
          onLoadError={(error) => {
            console.log(error)
            setIsPdfLibrarySupported(false)
          }}
          onSourceError={(error) => setPdfError('No pudimos cargar el PDF. Intenta actualizar tu navegador o descarga el archivo directamente.')}
          onLoadProgress={({ loaded, total }) => {
            setPdfLoadingPercent(Math.round((loaded / total) * 100))
          }}
          onLoadSuccess={onLoadSuccess}
          loading={(
            <PDFLoader
              fileName={fileName}
              progress={pdfLoadingPercent}
            />
          )}
          renderMode="canvas"
        >
          <FullScreen
            handle={handle}
            onChange={(state, handle) => {
              if (state) {
                setIsFullScreen(true);
              } else {
                setIsFullScreen(false);
              }
            }}
            className={`pdf-wrapper ${isFullScreen ? 'fullscreen-mode' : ''}`}
          >
            <MainWrapper className={`${isFullScreen ? 'fullscreen-mode' : ''}`}>

              {pdfLoadingPercent < 100 && (
                <LoaderWrapper>
                  <LoaderText>
                    {pdfLoadingPercent}
                  </LoaderText>
                </LoaderWrapper>
              )}

              {!!pdfError && (
                <LoaderWrapper>
                  <LoaderText>
                    {pdfError}
                  </LoaderText>
                </LoaderWrapper>
              )}

              <ThumbnailsWrapper style={{ opacity: !isFullScreen && !isMobile ? 1 : 0, zIndex: !isFullScreen && !isMobile ? 1 : -1, ...((isFullScreen || isMobile) && { width: 0 }) }}>
                <Scroll isMobile={isMobile} ref={thumbnailsListRef} width={pdfWidth} height={pdfHeight} className={`${isFullScreen ? 'fullscreen-mode' : ''}`}>
                  <ThumbnailsList >
                    <Thumbnail totalPages={totalPages} setCurrentPage={setCurrentPage} currentPage={currentPage} />
                  </ThumbnailsList>
                </Scroll>
              </ThumbnailsWrapper>

              <PreviewMainWrapper className={`pages ${isFullScreen ? 'fullscreen-mode' : ''}`}>
                <PreviewWrapper isMobile={isMobile} ref={mainWrapRef} height={pdfHeight + 4} width={pdfWidth} className={`${isFullScreen ? 'fullscreen-mode' : ''}`} >
                  <Page
                    pageNumber={currentPage}
                    scale={pdfZoom}
                    // width={pdfWidth}
                    // height={pdfHeight}
                    // renderAnnotationLayer={false}
                    renderTextLayer={false}
                    renderAnnotationLayer={true}
                    onRenderSuccess={() => {
                      const page = document.querySelectorAll('.pages .react-pdf__Page')[0];
                      if (page) {
                        const links = page.querySelectorAll('a');
                        links.forEach((link) => {
                          link.setAttribute('target', '_blank');
                          link.setAttribute('rel', 'noopener noreferrer');
                        });
                      }
                    }}
                  />
                </PreviewWrapper>

                <div style={{ height: 20 }} />

                <Toolbar ref={toolbarRef} className={`${isFullScreen ? 'fullscreen-mode' : ''}`} >
                  <Button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    skin="primary"
                  >
                    <Icon icon={GoBackIconSVG} color="#FFFFFF" />
                  </Button>

                  <Tools>
                    <Pagination>
                      {!isFullScreen && !isMobile && <span>Página</span>} <Input
                        ref={inputRef}
                        onBlur={handleInputBlur}
                      /> <span>/ {totalPages}</span>
                    </Pagination>

                    <Tools style={{ justifyContent: 'flex-end' }}>
                      {(!blockDownloadFiles || isOwner) && (
                        <DownloadLink
                          target="_blank"
                          href={fileUrl}
                          key={key}
                        >
                          <Icon icon={downloadFileIcon} color="#000000" />
                          {/* {!isMobile && (<span style={{ marginLeft: "4px", fontWeight: 500 }}>Descargar</span>)} */}

                        </DownloadLink>
                      )}
                      <Button onClick={zoomIn}>
                        <img src={ZoomInSVG} alt="Zoom in" />
                      </Button>
                      <Button onClick={zoomOut}>
                        <img src={ZoomOutSVG} alt="Zoom out" />
                      </Button>

                      {isFullScreen ? (
                        <Button
                          onClick={exitFullScreen}
                        >
                          <Icon icon={CollapsePdfSVG} color="#000000" />
                        </Button>
                      ) : (
                        <Button
                          onClick={enterFullScreen}
                        >
                          <Icon icon={SizePdfSVG} color="#000000" />
                        </Button>
                      )}
                    </Tools>

                    <Button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      skin="primary"
                    >
                      <Icon icon={GoFowardIconSVG} color="#FFFFFF" />
                    </Button>
                  </Tools>
                </Toolbar>
              </PreviewMainWrapper>
            </MainWrapper>
          </FullScreen>
        </Document>

      ) : (
        <PdfLink
          target="_blank"
          href={fileUrl}
          key={`attached-file-${slugify(fileName)}`}
        >
          <Icon icon={fileUrl} color="white" />
          Clickea aqui para descargar: {fileName}
        </PdfLink>
      )}

      <div style={{ height: 50 }} />

    </>
  );
};

export default PdfViewer;
